import { IItemDelta } from '../../../domain/value-object/ItemDeltaInputVO'
import { TreeSource } from '../../containers/BulkSheetView/lib/tree'
import { TreeRow } from '../../containers/BulkSheetView/model'
import {
  ProjectCreateProps,
  ProjectDetail,
  ProjectUpdateDeltaProps,
} from '../../../infrastructure/api/project'
import { ProjectFormModel } from '../ProjectV2/model'
import {
  AggregateField,
  AggregateTarget,
} from '../../../domain/entity/WbsItemEntity'
import { GanttParameterVO } from '../../../domain/value-object/GanttParameterVO'
import { CalendarDateVO } from '../../../domain/value-object/CalendarDateVO'
import { WorkLoadUnitState } from '../../hooks/useWorkloadUnit'
import { ProjectPlanCumulation } from '../../../lib/functions/projectPlan'
import { InputError } from '../../containers/BulkSheetView/lib/validation'

export const PROGRAM_MANAGEMENT_ROW_TYPE = {
  PROGRAM: 'PROGRAM',
  GROUP: 'GROUP',
  PROJECT: 'PROJECT',
  WORKGROUP: 'WORKGROUP',
  PROCESS: 'PROCESS',
} as const

export type ProgramManagementRowType =
  (typeof PROGRAM_MANAGEMENT_ROW_TYPE)[keyof typeof PROGRAM_MANAGEMENT_ROW_TYPE]

export type ProgramManagementProgress = {
  total: number
  scheduledCompleted: number
  completed: number
  delayed: number
  preceding: number
}

export type ProgramManagementProgressSet = {
  deliverableCount: ProgramManagementProgress
  deliverableWorkload: ProgramManagementProgress
  taskCount: ProgramManagementProgress
  taskWorkload: ProgramManagementProgress
}

export type ProgramManagementResource = {
  totalResource: number
  investedResource: number
  scheduledResource: number
  actualResource: number
}

// 投入工数(実績工数, 時間)。他のリソース系がプロジェクト行のみなのに対し、
// 投入工数は工程行(PROCESS/WORKGROUP)にも値が入るため専用構造に分けている。
export type ProgramManagementProductivity = {
  actualHour: number
}

export type ProgramManagementProfitLoss = {
  totalBudget: number
  scheduledUsedBudget: number
  actualUsedBudget: number
  remainingBudget: number
  totalRevenue: number
  scheduledRevenue: number
  actualRevenue: number
  forecastRevenue: number
}

export type ProgramManagementResponse =
  TreeSource<ProgramManagementResponse> & {
    uuid: string
    lockVersion: number
    children: ProgramManagementResponse[]
    type: ProgramManagementRowType
    groupUuid?: string
    projectUuid?: string
    /**
     * 行に対応する WbsItem の UUID。
     * 行の uuid は projectPlanUuid のため、クリティカルパス保存（wbsItemUuid を要求）に必要。
     */
    wbsItemUuid?: string
    iconUrl?: string
    code?: string
    displayName?: string
    status?: string
    projectManager?: { uuid: string; name: string; iconUrl?: string }
    scheduledDate?: { startDate?: string; endDate?: string }
    progress?: ProgramManagementProgressSet
    resource?: ProgramManagementResource
    profitLoss?: ProgramManagementProfitLoss
    productivity?: ProgramManagementProductivity
    projectPlanCumulation?: ProjectPlanCumulation
  }

export type ProgramManagementRow = TreeRow & {
  type: ProgramManagementRowType
  groupUuid?: string
  projectUuid?: string
  /**
   * 行に対応する WbsItem の UUID。
   * 行の uuid は projectPlanUuid のため、クリティカルパス保存（wbsItemUuid を要求）に必要。
   */
  wbsItemUuid?: string
  iconUrl?: string
  lockVersion: number
  code?: string
  displayName?: string
  status?: string
  projectManager?: { uuid: string; name: string; iconUrl?: string }
  scheduledDate?: { startDate?: string; endDate?: string }
  /** GROUP行でユーザーが予定日を手動編集したかどうか */
  scheduledDateEdited?: boolean
  /** 予定開始日が配下からの集計で補完されたものか（表示用: 薄グレー文字にする） */
  scheduledStartDateAggregated?: boolean
  /** 予定終了日が配下からの集計で補完されたものか（表示用: 薄グレー文字にする） */
  scheduledEndDateAggregated?: boolean
  progress?: ProgramManagementProgressSet
  resource?: ProgramManagementResource
  profitLoss?: ProgramManagementProfitLoss
  productivity?: ProgramManagementProductivity
  projectPlanCumulation?: ProjectPlanCumulation
  // --- プロジェクト単票ダイアログで編集する新規/更新プロジェクトの下書き ---
  /** ダイアログで編集したプロジェクトの入力値（新規作成・既存編集どちらも保持）。 */
  projectDraft?: ProjectFormModel
  /** 既存プロジェクト編集時の元 detail（保存時の差分算出用）。新規では未設定。 */
  projectOriginal?: ProjectDetail
  /** このプロジェクトを新規作成するか（true: CreateProject / false: 既存参照 or 更新）。 */
  isNewProject?: boolean
}

export type Program = {
  uuid: string
  code: string
  displayName: string
}

/** プロジェクト選択用の型 */
export type ProjectForSelection = {
  uuid: string
  code: string
  displayName: string
  iconUrl?: string
}

// --- デルタバッチリクエスト型 ---

// PROJECT行に同梱するプロジェクトの作成/更新ペイロード。
// create: 新規プロジェクトを同時作成 / update: 既存プロジェクトを同時更新。
export type ProgramItemProjectPayload = {
  create?: ProjectCreateProps
  update?: ProjectUpdateDeltaProps
}

export type ProgramManagementAddedItem = {
  uuid: string
  programUuid: string
  type: ProgramManagementRowType
  parentUuid?: string
  prevSiblingUuid?: string
  programItemGroup?: {
    uuid: string
    programUuid: string
    code?: string
    displayName: string
    scheduledDate?: { startDate?: string; endDate?: string }
  }
  projectUuid?: string
  // PROJECT行で新規作成/同時更新する場合のプロジェクトペイロード。
  project?: ProgramItemProjectPayload
}

export type ProgramManagementEditedItem = {
  uuid: string
  type: ProgramManagementRowType
  parentUuid?: IItemDelta<string>
  prevSiblingUuid?: IItemDelta<string>
  programItemGroup?: {
    uuid: string
    displayName?: IItemDelta<string>
    scheduledDate?: IItemDelta<{
      startDate?: string
      endDate?: string
    }>
  }
  // PROJECT行で既存プロジェクトを同時更新する場合の差分。
  // 別プロジェクトへの付け替えは仕様上できないため、更新（delta）のみ。
  project?: ProjectUpdateDeltaProps
}

export type ProgramManagementDeletedItem = {
  uuid: string
}

/** プログラム本体（予定日）の更新。サーバーの UpdateProgramDelta.Input に対応 */
export type ProgramManagementProgramDelta = {
  uuid: string
  scheduledStartDate?: IItemDelta<string>
  scheduledEndDate?: IItemDelta<string>
}

export type ProgramManagementDeltaBatchRequest = {
  program?: ProgramManagementProgramDelta
  added: ProgramManagementAddedItem[]
  edited: ProgramManagementEditedItem[]
  deleted: ProgramManagementDeletedItem[]
}

/** ページ状態を valueGetter に渡すための AG Grid context */
export type ProgramManagementGridContext = {
  aggregateTarget: AggregateTarget
  aggregateField: AggregateField
  dateFormat: string
  workloadUnitState?: WorkLoadUnitState
  isRowHeightFixed: boolean
  ganttParameter?: GanttParameterVO
  ganttDisplayTimeScale?: CalendarDateVO[]
  ganttTimeScale?: CalendarDateVO[]
  /** イナズマ線表示中か（true のとき GanttBackground が当日線を灰色破線にする） */
  progressLineEnabled?: boolean
  /** ガントバー（予定線）のドラッグ編集確定（PROGRAM/GROUP 行）。GanttSlider が呼ぶ */
  onGanttBarDateEdited?: (
    row: ProgramManagementRow,
    dateTerm: { startDate?: string; endDate?: string }
  ) => void
  errors?: InputError
  draggableNodeId?: string
  onTree?: boolean
}
